import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { 
  FiHome, FiFileText, FiPlusCircle, FiBookOpen, FiBell, 
  FiLogOut, FiPhone, FiMapPin, FiCalendar, FiDollarSign, 
  FiPackage, FiCheck, FiSearch, FiCopy, FiPrinter
} from 'react-icons/fi';
import Swal from 'sweetalert2';
import { 
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid 
} from 'recharts';

const API_URL = 'https://app.thundermaxhome.com/api.php';

export default function SalesApp() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // States สำหรับแต่ละฟังก์ชัน
  const [products, setProducts] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [searchCatalog, setSearchCatalog] = useState('');
  
  // State สรุปยอด Dashboard
  const [stats, setStats] = useState({
    totalSales: 0,
    activeReminders: 0,
    completedJobs: 0,
    commission: 0
  });

  // State ฟอร์มสร้างออเดอร์
  const [orderForm, setOrderForm] = useState({
    customerName: '',
    phone: '',
    address: '',
    job_type: 'aircon',
    problem: 'ติดตั้งแอร์ใหม่',
    price: '',
    appointmentDate: '',
    appointmentTime: '09:00'
  });

  // State เครื่องมือสร้างใบเสนอราคา (Smart Quotation)
  const [quoteForm, setQuoteForm] = useState({
    customerName: 'ลูกค้า B2B ทั่วไป',
    companyName: 'บริษัท ออฟฟิศ ดีเวลลอปเม้นท์ จำกัด',
    phone: '081-234-5678',
    address: 'แขวงพญาไท เขตราชเทวี กรุงเทพฯ',
    selectedProductId: '',
    quantity: 1,
    pipeLength: 4, // เมตรที่แถม ปกติ 4
    extraPipeCost: 500, // ต่อเมตร
    laborCost: 2500,
    discount: 0
  });

  // โหลดข้อมูลจาก API
  useEffect(() => {
    fetchProducts();
    fetchReminders();
    fetchDashboardStats();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await fetch(`${API_URL}?action=get_products`);
      const data = await res.json();
      if (data.success) {
        setProducts(data.data);
      }
    } catch (err) {
      console.error("Error products:", err);
    }
  };

  const fetchReminders = async () => {
    try {
      const res = await fetch(`${API_URL}?action=get_sales_reminders`);
      const data = await res.json();
      if (data.success) {
        setReminders(data.data);
      }
    } catch (err) {
      console.error("Error reminders:", err);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      // ดึงงานทั้งหมดเพื่อคำนวณยอดขายของเซลส์ (ดึงข้อมูลงานจริงจากหลังบ้าน)
      const res = await fetch(`${API_URL}?action=get_jobs`);
      const data = await res.json();
      if (data.success && data.data) {
        const completed = data.data.filter(j => j.status === 'completed');
        const salesSum = completed.reduce((sum, j) => sum + (Number(j.price) || 0), 0);
        
        // สมมติค่า Commission 5% ของยอดรวม
        const comm = salesSum * 0.05;
        
        setStats({
          totalSales: salesSum,
          activeReminders: reminders.length,
          completedJobs: completed.length,
          commission: comm
        });
      }
    } catch (err) {
      console.error("Error stats:", err);
    }
  };

  // อัปเดตสถิติหลังจองงาน/เตือนความจำเปลี่ยน
  useEffect(() => {
    setStats(prev => ({ ...prev, activeReminders: reminders.length }));
  }, [reminders]);

  // ฟังก์ชันจองคิวออเดอร์ใหม่
  const handleCreateOrder = async (e) => {
    e.preventDefault();
    Swal.fire({ title: 'กำลังสร้างออเดอร์...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    
    try {
      const payload = {
        ...orderForm,
        status: 'pending_approval' // ส่งไปแอดมินอนุมัติ
      };
      
      const res = await fetch(`${API_URL}?action=add_job`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      
      if (data.success) {
        Swal.fire({ icon: 'success', title: 'ส่งออเดอร์ไปที่ Admin สำเร็จ!', text: 'คิวงานใหม่ถูกส่งเข้าหลังบ้านแล้ว', timer: 2000, showConfirmButton: false });
        
        // เคลียร์ฟอร์ม
        setOrderForm({
          customerName: '',
          phone: '',
          address: '',
          job_type: 'aircon',
          problem: 'ติดตั้งแอร์ใหม่',
          price: '',
          appointmentDate: '',
          appointmentTime: '09:00'
        });
        
        // รีเฟรชข้อมูล
        fetchDashboardStats();
        setActiveTab('dashboard');
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      Swal.fire('ล้มเหลว', err.message || 'ไม่สามารถส่งออเดอร์ได้', 'error');
    }
  };

  // คลิกแจ้งเตือนล้างแอร์ แล้วเอาข้อมูลไปกรอกหน้าจองคิวทันที
  const handleFollowUpReminder = (reminder) => {
    setOrderForm({
      customerName: reminder.customerName,
      phone: reminder.phone,
      address: reminder.address,
      job_type: 'aircon',
      problem: 'ล้างแอร์ทำความสะอาดประจำรอบ 6 เดือน (ลูกค้าเก่าถัดจากงาน ID: ' + reminder.id + ')',
      price: '600', // ราคาเริ่มต้น
      appointmentDate: '',
      appointmentTime: '09:00'
    });
    
    // คัดลอกข้อความทักทายไปที่คลิปบอร์ดเพื่อความสะดวกของเซลส์
    const greetingMsg = `สวัสดีครับคุณ ${reminder.customerName} จากร้าน Thunder Maxhome นะครับ ตอนนี้เครื่องปรับอากาศที่คุณเคยทำบริการไว้ครบกำหนดรอบล้างแอร์ 6 เดือนแล้วนะครับ เพื่อป้องกันการสะสมฝุ่นและประหยัดค่าไฟช่วงนี้ สนใจจองคิวล้างแอร์เพิ่มเติมไหมครับ?`;
    navigator.clipboard.writeText(greetingMsg);
    
    Swal.fire({
      icon: 'info',
      title: 'คัดลอกข้อความทักทายสำเร็จ!',
      text: 'คุณสามารถนำข้อความไปวางคุยกับลูกค้าใน Line/Messenger ได้ทันที พร้อมระบบกรอกฟอร์มออเดอร์ใหม่ให้โดยอัตโนมัติ',
      confirmButtonText: 'ตกลง'
    });

    setActiveTab('create_order');
  };

  // ดึงข้อมูลคำนวณใบเสนอราคา
  const getQuotationSummary = () => {
    const selectedProd = products.find(p => p.id == quoteForm.selectedProductId);
    const basePrice = selectedProd ? Number(selectedProd.price) : 0;
    const prodTotal = basePrice * quoteForm.quantity;
    
    // คำนวณค่าท่อส่วนเกิน (ฟรี 4 เมตรแรก)
    const extraPipeLength = Math.max(0, quoteForm.pipeLength - 4);
    const extraPipeTotal = extraPipeLength * quoteForm.extraPipeCost * quoteForm.quantity;
    
    const laborTotal = Number(quoteForm.laborCost) * quoteForm.quantity;
    
    const subtotal = prodTotal + extraPipeTotal + laborTotal - Number(quoteForm.discount || 0);
    const vat = subtotal * 0.07;
    const grandTotal = subtotal + vat;

    return {
      selectedProd,
      prodTotal,
      extraPipeTotal,
      extraPipeLength,
      laborTotal,
      subtotal,
      vat,
      grandTotal
    };
  };

  // พิมพ์ใบเสนอราคาทางหน้าต่างพิมพ์บราวเซอร์แบบสวยงาม
  const handlePrintQuotation = () => {
    const sum = getQuotationSummary();
    if (!sum.selectedProd) {
      Swal.fire('ข้อผิดพลาด', 'กรุณาเลือกรุ่นสินค้าในตารางก่อนจัดทำใบเสนอราคา', 'warning');
      return;
    }

    const printWindow = window.open('', '_blank');
    const quoteNo = 'QT-' + new Date().getFullYear() + String(new Date().getMonth() + 1).padStart(2, '0') + String(new Date().getDate()).padStart(2, '0') + '-' + Math.floor(100 + Math.random() * 900);
    
    printWindow.document.write(`
      <html>
      <head>
        <title>ใบเสนอราคา - ${sum.selectedProd.model_name}</title>
        <style>
          body { font-family: 'Sarabun', sans-serif; color: #333; padding: 40px; line-height: 1.5; }
          .header-table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
          .logo-area { font-size: 24px; font-weight: bold; color: #2563eb; }
          .title-area { font-size: 22px; font-weight: bold; text-align: right; }
          .info-table { width: 100%; border-collapse: collapse; margin-bottom: 35px; font-size: 14px; }
          .info-table td { padding: 5px 0; vertical-align: top; }
          .details-table { width: 100%; border-collapse: collapse; margin-bottom: 40px; font-size: 14px; }
          .details-table th { background: #f1f5f9; padding: 12px; font-weight: bold; text-align: left; border: 1px solid #cbd5e1; }
          .details-table td { padding: 12px; border: 1px solid #cbd5e1; }
          .summary-table { width: 45%; margin-left: 55%; border-collapse: collapse; font-size: 14px; }
          .summary-table td { padding: 8px 12px; border: 1px solid #cbd5e1; }
          .footer-section { margin-top: 60px; display: flex; justify-content: space-between; font-size: 14px; }
          .sign-box { border-top: 1px solid #000; width: 220px; text-align: center; padding-top: 10px; margin-top: 50px; }
          .badge { border: 1px solid #2563eb; color: #2563eb; padding: 3px 8px; border-radius: 4px; font-size: 12px; display: inline-block; margin-bottom: 10px; font-weight: bold; }
        </style>
      </head>
      <body>
        <table class="header-table">
          <tr>
            <td class="logo-area">⚡ THUNDER MAXHOME</td>
            <td class="title-area">ใบเสนอราคา / QUOTATION</td>
          </tr>
        </table>

        <table class="info-table">
          <tr>
            <td style="width: 50%">
              <strong>ผู้เสนอราคา:</strong><br>
              ห้างหุ้นส่วนจำกัด ธันเดอร์ แมกซ์โฮม แอร์คอนดิชั่นนิ่ง<br>
              เบอร์โทรศัพท์: 02-123-4567<br>
              อีเมล: sales@thundermaxhome.com
            </td>
            <td style="width: 50%; text-align: right;">
              <strong>เลขที่เอกสาร:</strong> ${quoteNo}<br>
              <strong>วันที่เสนอราคา:</strong> ${new Date().toLocaleDateString('th-TH')}<br>
              <strong>จัดทำโดย:</strong> ${user?.name || 'ทีมเซลส์'} (Sales ID: ${user?.uid})
            </td>
          </tr>
          <tr>
            <td style="padding-top: 15px;">
              <strong>ลูกค้าผู้รับข้อเสนอ:</strong><br>
              ชื่อลูกค้า: ${quoteForm.customerName}<br>
              ชื่อบริษัท: ${quoteForm.companyName || '-'}<br>
              เบอร์โทร: ${quoteForm.phone}<br>
              ที่อยู่หน้างาน: ${quoteForm.address}
            </td>
            <td></td>
          </tr>
        </table>

        <table class="details-table">
          <thead>
            <tr>
              <th style="width: 5%">ลำดับ</th>
              <th style="width: 50%">รายการสินค้า/รายละเอียด</th>
              <th style="width: 15%; text-align: right;">ราคา/หน่วย</th>
              <th style="width: 10%; text-align: center;">จำนวน</th>
              <th style="width: 20%; text-align: right;">จำนวนเงิน (บาท)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style="text-align: center;">1</td>
              <td><strong>เครื่องปรับอากาศ ${sum.selectedProd.brand} รุ่น ${sum.selectedProd.model_name}</strong><br>ขนาด ${sum.selectedProd.btu.toLocaleString()} BTU (รวมติดตั้งแถมอุปกรณ์มาตรฐาน)</td>
              <td style="text-align: right;">${Number(sum.selectedProd.price).toLocaleString()}</td>
              <td style="text-align: center;">${quoteForm.quantity}</td>
              <td style="text-align: right;">${sum.prodTotal.toLocaleString()}</td>
            </tr>
            <tr>
              <td style="text-align: center;">2</td>
              <td><strong>ค่าส่วนต่างท่อส่งน้ำยาแอร์เพิ่มเติ่ม (แถมฟรี 4 เมตรแรก)</strong><br>ความยาวรวม ${quoteForm.pipeLength} เมตร (ส่วนเกินคิด ${extraPipeLength} เมตร)</td>
              <td style="text-align: right;">${quoteForm.extraPipeCost.toLocaleString()}</td>
              <td style="text-align: center;">${quoteForm.quantity}</td>
              <td style="text-align: right;">${sum.extraPipeTotal.toLocaleString()}</td>
            </tr>
            <tr>
              <td style="text-align: center;">3</td>
              <td><strong>ค่าแรงงานติดตั้งและเดินสายระบบไฟฟ้าภายนอก</strong><br>บริการติดตั้งมาตรฐานโดยช่างผู้เชี่ยวชาญ</td>
              <td style="text-align: right;">${Number(quoteForm.laborCost).toLocaleString()}</td>
              <td style="text-align: center;">${quoteForm.quantity}</td>
              <td style="text-align: right;">${sum.laborTotal.toLocaleString()}</td>
            </tr>
            ${quoteForm.discount > 0 ? `
            <tr style="color: red;">
              <td style="text-align: center;">4</td>
              <td><strong>ส่วนลดพิเศษ (โปรโมชั่นเซลส์)</strong></td>
              <td style="text-align: right;">-${Number(quoteForm.discount).toLocaleString()}</td>
              <td style="text-align: center;">1</td>
              <td style="text-align: right;">-${Number(quoteForm.discount).toLocaleString()}</td>
            </tr>` : ''}
          </tbody>
        </table>

        <table class="summary-table">
          <tr>
            <td><strong>รวมค่าบริการ (ก่อนภาษี)</strong></td>
            <td style="text-align: right;">${sum.subtotal.toLocaleString()} บาท</td>
          </tr>
          <tr>
            <td>ภาษีมูลค่าเพิ่ม (VAT 7%)</td>
            <td style="text-align: right;">${sum.vat.toLocaleString()} บาท</td>
          </tr>
          <tr style="background: #f1f5f9; font-weight: bold; font-size: 16px;">
            <td>ยอดสุทธิรวมทั้งสิ้น</td>
            <td style="text-align: right; color: #2563eb;">${sum.grandTotal.toLocaleString()} บาท</td>
          </tr>
        </table>

        <div class="footer-section">
          <div>
            <strong>เงื่อนไขการชำระเงิน:</strong><br>
            - มัดจำ 30% เมื่อทำสัญญาเสนอราคา<br>
            - ชำระส่วนที่เหลือหน้างานหลังติดตั้งเสร็จสิ้น
          </div>
          <div>
            <div class="sign-box">
              ผู้อนุมัติเสนอราคา / เซลส์ผู้ดูแล
            </div>
          </div>
        </div>
        
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // แผนภูมิยอดขายจำลองรายเดือน
  const salesChartData = [
    { name: 'ม.ค.', ยอดขาย: stats.totalSales * 0.8 },
    { name: 'ก.พ.', ยอดขาย: stats.totalSales * 0.95 },
    { name: 'มี.ค.', ยอดขาย: stats.totalSales * 1.2 },
    { name: 'เม.ย.', ยอดขาย: stats.totalSales * 1.5 },
    { name: 'พ.ค.', ยอดขาย: stats.totalSales * 1.1 },
    { name: 'มิ.ย.', ยอดขาย: stats.totalSales }
  ];

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 flex flex-col pb-20 md:pb-0 md:pl-64 font-sans">
      
      {/* 📱 Sidebar/Header สำหรับหน้าจอมือถือและ PC */}
      <aside className="hidden md:flex flex-col w-64 bg-slate-950 border-r border-slate-800 p-6 fixed top-0 bottom-0 left-0">
        <div className="mb-8">
          <h1 className="text-2xl font-black text-blue-500 tracking-wider">THUNDER <span className="text-white">SALES</span></h1>
          <p className="text-xs text-slate-500 font-bold mt-1 uppercase tracking-widest">ระบบแอพเซลส์ปิดการขาย</p>
        </div>
        
        <div className="flex items-center gap-3 p-3 bg-slate-900 border border-slate-800 rounded-xl mb-6">
          <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center font-bold text-white">
            {user?.name?.charAt(0) || 'S'}
          </div>
          <div>
            <p className="font-bold text-sm text-slate-200">{user?.name || 'คุณเซลส์'}</p>
            <p className="text-[10px] text-blue-400 font-semibold uppercase">{user?.role === 'sales' ? '💼 ทีมเซลส์ฝ่ายขาย' : 'สิทธิ์พนักงาน'}</p>
          </div>
        </div>

        <nav className="flex-1 space-y-2">
          <button onClick={() => setActiveTab('dashboard')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === 'dashboard' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-900 hover:text-white'}`}>
            <FiHome /> แดชบอร์ดเป้าขาย
          </button>
          <button onClick={() => setActiveTab('create_order')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === 'create_order' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-900 hover:text-white'}`}>
            <FiPlusCircle /> สร้างออเดอร์ใหม่
          </button>
          <button onClick={() => setActiveTab('quotation')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === 'quotation' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-900 hover:text-white'}`}>
            <FiFileText /> Smart Quotation
          </button>
          <button onClick={() => setActiveTab('catalog')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === 'catalog' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-900 hover:text-white'}`}>
            <FiBookOpen /> คลังสินค้า & สต๊อก
          </button>
          <button onClick={() => setActiveTab('reminders')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all relative ${activeTab === 'reminders' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-900 hover:text-white'}`}>
            <FiBell /> รอบล้างแอร์ลูกค้าเก่า
            {reminders.length > 0 && <span className="absolute right-3 top-3 w-5 h-5 rounded-full bg-red-600 text-white text-[10px] flex items-center justify-center font-bold">{reminders.length}</span>}
          </button>
        </nav>

        <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold text-red-400 hover:bg-red-500/10 transition-all mt-auto">
          <FiLogOut /> ออกจากระบบ
        </button>
      </aside>

      {/* 📱 Header สำหรับมือถือ */}
      <header className="md:hidden bg-slate-950 border-b border-slate-800 p-4 sticky top-0 z-50 flex justify-between items-center">
        <div>
          <h1 className="text-lg font-black text-blue-500">THUNDER <span className="text-white">SALES</span></h1>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400 font-bold">{user?.name}</span>
          <button onClick={logout} className="text-red-400 hover:text-red-300">
            <FiLogOut size={18} />
          </button>
        </div>
      </header>

      {/* 🌟 หน้าจอหลัก */}
      <main className="flex-1 p-4 md:p-8 overflow-y-auto max-w-6xl mx-auto w-full">
        
        {/* =========================================================
            📊 TAB: DASHBOARD
            ========================================================= */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6 animate-fade-in">
            <div>
              <h2 className="text-2xl font-black text-white">📊 แดชบอร์ดฝ่ายขาย</h2>
              <p className="text-xs text-slate-400 mt-1">สรุปคอมมิชชั่น ยอดขาย และเป้าเปรียบเทียบส่วนบุคคล</p>
            </div>

            {/* การ์ดสถิติ 4 ใบ */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-slate-950/50 border border-slate-800 p-4 rounded-2xl flex flex-col justify-between h-28">
                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">ยอดขายสะสมเดือนนี้</span>
                <span className="text-xl font-black text-emerald-400">฿{stats.totalSales.toLocaleString()}</span>
                <span className="text-[9px] text-slate-400">ยอดที่ส่งมอบสำเร็จและชำระแล้ว</span>
              </div>
              <div className="bg-slate-950/50 border border-slate-800 p-4 rounded-2xl flex flex-col justify-between h-28">
                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">ค่าคอมมิชชั่นสะสม (5%)</span>
                <span className="text-xl font-black text-blue-400">฿{stats.commission.toLocaleString()}</span>
                <span className="text-[9px] text-slate-400">รับเป็นเงินโบนัสปลายเดือน</span>
              </div>
              <div className="bg-slate-950/50 border border-slate-800 p-4 rounded-2xl flex flex-col justify-between h-28">
                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">งานที่ปิดการขายได้</span>
                <span className="text-xl font-black text-amber-400">{stats.completedJobs} งาน</span>
                <span className="text-[9px] text-slate-400">คิวติดตั้งแอร์ & บริการล้างสำเร็จ</span>
              </div>
              <div className="bg-slate-950/50 border border-slate-800 p-4 rounded-2xl flex flex-col justify-between h-28 relative">
                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">รอบเสนอล้างแอร์ลูกค้าเก่า</span>
                <span className="text-xl font-black text-rose-400">{stats.activeReminders} รายการ</span>
                {stats.activeReminders > 0 && <span className="absolute right-3 top-3 w-2.5 h-2.5 rounded-full bg-rose-500 animate-ping"></span>}
                <span className="text-[9px] text-slate-400">ถึงเวลาทักเพื่อเสนอล้างแอร์ต่อ</span>
              </div>
            </div>

            {/* เป้าหมายการขายประจำเดือน */}
            <div className="bg-slate-950/50 border border-slate-800 p-6 rounded-2xl space-y-4">
              <div>
                <h3 className="text-sm font-bold text-slate-300">🎯 ความคืบหน้าเป้าหมายการขายประจำเดือน (Target Progress)</h3>
                <p className="text-[11px] text-slate-500">เป้ายอดขายสะสม ฿100,000 ต่อเดือนสำหรับทีมเซลส์</p>
              </div>
              
              <div>
                <div className="flex justify-between text-xs font-bold mb-1.5">
                  <span>ความสำเร็จ: {Math.min(100, Math.round((stats.totalSales / 100000) * 100))}%</span>
                  <span>ยอดปัจจุบัน ฿{stats.totalSales.toLocaleString()} / เป้าหมาย ฿100,000</span>
                </div>
                <div className="w-full bg-slate-800 h-3 rounded-full overflow-hidden">
                  <div className="bg-gradient-to-r from-blue-600 to-cyan-500 h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, (stats.totalSales / 100000) * 100)}%` }}></div>
                </div>
              </div>
            </div>

            {/* กราฟยอดขายรายเดือน */}
            <div className="bg-slate-950/50 border border-slate-800 p-6 rounded-2xl">
              <h3 className="text-sm font-bold text-slate-300 mb-4">📈 แผนภูมิมูลค่ายอดขายใน 6 เดือนที่ผ่านมา</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={salesChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="name" stroke="#64748b" style={{ fontSize: 12 }} />
                    <YAxis stroke="#64748b" style={{ fontSize: 12 }} />
                    <Tooltip contentStyle={{ backgroundColor: '#090d16', borderColor: '#1e293b', color: '#fff' }} />
                    <Bar dataKey="ยอดขาย" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* =========================================================
            ➕ TAB: CREATE ORDER (สร้างออเดอร์)
            ========================================================= */}
        {activeTab === 'create_order' && (
          <div className="space-y-6 animate-fade-in">
            <div>
              <h2 className="text-2xl font-black text-white">📝 บันทึกข้อมูลใบสั่งซื้อ/จองคิวใหม่</h2>
              <p className="text-xs text-slate-400 mt-1">ส่งคำสั่งจองงานไปหลังบ้านให้แอดมินทำการตรวจสอบและอนุมัติจ่ายงานช่าง</p>
            </div>

            <form onSubmit={handleCreateOrder} className="bg-slate-950/50 border border-slate-800 p-6 rounded-2xl space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-400 uppercase">ชื่อ-นามสกุล ลูกค้า</label>
                  <input type="text" required value={orderForm.customerName} onChange={e => setOrderForm({...orderForm, customerName: e.target.value})} className="w-full bg-slate-900 border border-slate-800 text-sm rounded-xl py-3 px-4 outline-none focus:border-blue-500 transition text-slate-200" placeholder="ระบุชื่อลูกค้าบ้าน หรือ ออฟฟิศ" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-400 uppercase">เบอร์โทรศัพท์ติดต่อ</label>
                  <input type="tel" required value={orderForm.phone} onChange={e => setOrderForm({...orderForm, phone: e.target.value})} className="w-full bg-slate-900 border border-slate-800 text-sm rounded-xl py-3 px-4 outline-none focus:border-blue-500 transition text-slate-200" placeholder="เบอร์โทรมือถือลูกค้า 10 หลัก" />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-400 uppercase">ที่อยู่หน้างานและจุดสังเกต</label>
                <input type="text" required value={orderForm.address} onChange={e => setOrderForm({...orderForm, address: e.target.value})} className="w-full bg-slate-900 border border-slate-800 text-sm rounded-xl py-3 px-4 outline-none focus:border-blue-500 transition text-slate-200" placeholder="บ้านเลขที่ ถนน ซอย แขวง เขต" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-400 uppercase">ประเภทบริการ</label>
                  <select value={orderForm.job_type} onChange={e => setOrderForm({...orderForm, job_type: e.target.value})} className="w-full bg-slate-900 border border-slate-800 text-sm rounded-xl py-3 px-4 outline-none focus:border-blue-500 transition text-slate-200">
                    <option value="aircon">ติดตั้งหรือล้างแอร์</option>
                    <option value="electric">ระบบไฟฟ้า</option>
                    <option value="contractor">รับเหมา/อื่นๆ</option>
                  </select>
                </div>
                
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-400 uppercase">วันที่เวลานัดหมาย</label>
                  <div className="flex gap-2">
                    <input type="date" required value={orderForm.appointmentDate} onChange={e => setOrderForm({...orderForm, appointmentDate: e.target.value})} className="flex-1 bg-slate-900 border border-slate-800 text-sm rounded-xl py-3 px-3 outline-none focus:border-blue-500 text-slate-200" />
                    <input type="time" required value={orderForm.appointmentTime} onChange={e => setOrderForm({...orderForm, appointmentTime: e.target.value})} className="w-24 bg-slate-900 border border-slate-800 text-sm rounded-xl py-3 px-3 outline-none focus:border-blue-500 text-slate-200" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-400 uppercase">ราคาเสนอขายปิดได้สุทธิ (บาท)</label>
                  <input type="number" required value={orderForm.price} onChange={e => setOrderForm({...orderForm, price: e.target.value})} className="w-full bg-slate-900 border border-slate-800 text-sm rounded-xl py-3 px-4 outline-none focus:border-blue-500 transition text-slate-200" placeholder="เช่น 600 หรือ 15900" />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-400 uppercase">ระบุลักษณะสินค้า อาการเสีย หรือข้อตกลงพิเศษ</label>
                <textarea rows="4" required value={orderForm.problem} onChange={e => setOrderForm({...orderForm, problem: e.target.value})} className="w-full bg-slate-900 border border-slate-800 text-sm rounded-xl py-3 px-4 outline-none focus:border-blue-500 transition text-slate-200" placeholder="เช่น สั่งแอร์ Daikin Max Inverter 12,000 BTU 1 ชุด หรือ อาการแอร์ไม่เย็น ล้างเครื่องปรับอากาศ 4 ตัว เป็นต้น"></textarea>
              </div>

              <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 rounded-xl shadow-lg transition-colors flex justify-center items-center gap-2">
                <FiCheck /> ส่งใบสั่งงานไปที่ระบบแอดมิน (Submit Order)
              </button>
            </form>
          </div>
        )}

        {/* =========================================================
            📑 TAB: SMART QUOTATION (จัดทำใบเสนอราคา)
            ========================================================= */}
        {activeTab === 'quotation' && (
          <div className="space-y-6 animate-fade-in">
            <div>
              <h2 className="text-2xl font-black text-white">📄 เครื่องมือจัดทำใบเสนอราคาด่วน</h2>
              <p className="text-xs text-slate-400 mt-1">คำนวณราคาสินค้า, ความยาวท่อ, ค่าแรง พร้อมภาษีมูลค่าเพิ่ม (VAT 7%) และสั่งพิมพ์ได้ทันที</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* ฟอร์มปรับค่าใบเสนอราคา */}
              <div className="lg:col-span-2 bg-slate-950/50 border border-slate-800 p-6 rounded-2xl space-y-4">
                <h3 className="text-sm font-bold text-slate-300 border-b border-slate-800 pb-2">✏️ ข้อมูลและองค์ประกอบราคาสินค้า</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-400">เลือกรุ่นสินค้าจากคลัง E-Catalog</label>
                    <select value={quoteForm.selectedProductId} onChange={e => setQuoteForm({...quoteForm, selectedProductId: e.target.value})} className="w-full bg-slate-900 border border-slate-800 text-sm rounded-xl py-3 px-4 outline-none focus:border-blue-500 text-slate-200 font-bold">
                      <option value="">-- กรุณาเลือกสินค้า --</option>
                      {products.map(p => (
                        <option key={p.id} value={p.id}>
                          [{p.brand}] {p.model_name} - ฿{Number(p.price).toLocaleString()} (คงเหลือ {p.stock})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-400">จำนวนเครื่อง</label>
                    <input type="number" min="1" value={quoteForm.quantity} onChange={e => setQuoteForm({...quoteForm, quantity: Math.max(1, Number(e.target.value))})} className="w-full bg-slate-900 border border-slate-800 text-sm rounded-xl py-3 px-4 outline-none focus:border-blue-500 text-slate-200" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-400">ความยาวท่อน้ำยาแอร์รวม (เมตร)</label>
                    <input type="number" min="4" value={quoteForm.pipeLength} onChange={e => setQuoteForm({...quoteForm, pipeLength: Math.max(4, Number(e.target.value))})} className="w-full bg-slate-900 border border-slate-800 text-sm rounded-xl py-3 px-4 outline-none focus:border-blue-500 text-slate-200" title="ท่อมาตรฐานแถมฟรี 4 เมตรแรก" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-400">ค่าท่อส่วนเกินฟรี (บาท/เมตร)</label>
                    <input type="number" value={quoteForm.extraPipeCost} onChange={e => setQuoteForm({...quoteForm, extraPipeCost: Number(e.target.value)})} className="w-full bg-slate-900 border border-slate-800 text-sm rounded-xl py-3 px-4 outline-none focus:border-blue-500 text-slate-200" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-400">ค่าแรงงานติดตั้ง/ชุด (บาท)</label>
                    <input type="number" value={quoteForm.laborCost} onChange={e => setQuoteForm({...quoteForm, laborCost: Number(e.target.value)})} className="w-full bg-slate-900 border border-slate-800 text-sm rounded-xl py-3 px-4 outline-none focus:border-blue-500 text-slate-200" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-400">ส่วนลดส่วนบุคคลหรือโปรโมชั่นเซลด่วน (บาท)</label>
                  <input type="number" value={quoteForm.discount} onChange={e => setQuoteForm({...quoteForm, discount: Number(e.target.value)})} className="w-full bg-slate-900 border border-slate-800 text-sm rounded-xl py-3 px-4 outline-none focus:border-blue-500 text-slate-200" />
                </div>

                <h3 className="text-sm font-bold text-slate-300 border-b border-slate-800 pb-2 pt-2">👥 ข้อมูลฝั่งลูกค้าปลายทาง</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-400">ชื่อลูกค้า/ผู้ติดต่อ</label>
                    <input type="text" value={quoteForm.customerName} onChange={e => setQuoteForm({...quoteForm, customerName: e.target.value})} className="w-full bg-slate-900 border border-slate-800 text-sm rounded-xl py-3 px-4 outline-none focus:border-blue-500 text-slate-200" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-400">ชื่อนิติบุคคล/ชื่อหน่วยงาน (กรณี B2B)</label>
                    <input type="text" value={quoteForm.companyName} onChange={e => setQuoteForm({...quoteForm, companyName: e.target.value})} className="w-full bg-slate-900 border border-slate-800 text-sm rounded-xl py-3 px-4 outline-none focus:border-blue-500 text-slate-200" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-400">เบอร์โทรศัพท์ลูกค้า</label>
                    <input type="text" value={quoteForm.phone} onChange={e => setQuoteForm({...quoteForm, phone: e.target.value})} className="w-full bg-slate-900 border border-slate-800 text-sm rounded-xl py-3 px-4 outline-none focus:border-blue-500 text-slate-200" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-400">ที่อยู่จัดส่งสินค้า/หน้างานติดตั้ง</label>
                    <input type="text" value={quoteForm.address} onChange={e => setQuoteForm({...quoteForm, address: e.target.value})} className="w-full bg-slate-900 border border-slate-800 text-sm rounded-xl py-3 px-4 outline-none focus:border-blue-500 text-slate-200" />
                  </div>
                </div>
              </div>

              {/* สรุปยอดเงินและปุ่มคำสั่งออกเอกสาร */}
              <div className="bg-slate-950/50 border border-slate-800 p-6 rounded-2xl flex flex-col justify-between space-y-6">
                <div>
                  <h3 className="text-sm font-bold text-slate-300 border-b border-slate-800 pb-2 mb-4">💰 ตารางผลคำนวณยอดเงินสุทธิ</h3>
                  
                  {quoteForm.selectedProductId ? (
                    <div className="space-y-3.5 text-sm">
                      <div className="flex justify-between text-slate-400">
                        <span>ราคาสินค้าแอร์แบรนด์</span>
                        <span>฿{getQuotationSummary().prodTotal.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-slate-400">
                        <span>ท่อส่วนเกิน ({getQuotationSummary().extraPipeLength} ม.)</span>
                        <span>฿{getQuotationSummary().extraPipeTotal.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-slate-400">
                        <span>ค่าแรงติดตั้ง</span>
                        <span>฿{getQuotationSummary().laborTotal.toLocaleString()}</span>
                      </div>
                      {quoteForm.discount > 0 && (
                        <div className="flex justify-between text-rose-400 font-semibold">
                          <span>หักส่วนลดพิเศษ</span>
                          <span>-฿{Number(quoteForm.discount).toLocaleString()}</span>
                        </div>
                      )}
                      <div className="border-t border-slate-800 my-2"></div>
                      <div className="flex justify-between text-slate-300 font-bold">
                        <span>ยอดรวมก่อนภาษี</span>
                        <span>฿{getQuotationSummary().subtotal.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-slate-400 text-xs">
                        <span>ภาษีมูลค่าเพิ่ม (VAT 7%)</span>
                        <span>฿{getQuotationSummary().vat.toLocaleString()}</span>
                      </div>
                      <div className="border-t border-slate-800 my-2"></div>
                      <div className="flex justify-between text-white font-black text-lg">
                        <span>ยอดชำระสุทธิรวม VAT</span>
                        <span className="text-blue-400">฿{getQuotationSummary().grandTotal.toLocaleString()}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 py-6 text-center">โปรดระบุสินค้าด้านซ้ายเพื่อดูตารางราคารวม</p>
                  )}
                </div>

                <div className="space-y-2 mt-6">
                  <button onClick={handlePrintQuotation} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 rounded-xl shadow-lg transition flex justify-center items-center gap-2">
                    <FiPrinter /> ออกเอกสารใบเสนอราคา (Print PDF)
                  </button>
                  <p className="text-[10px] text-slate-500 text-center">พิมพ์หรือเซฟเป็นไฟล์ PDF นำไปปิดการขายได้ทันที</p>
                </div>
              </div>

            </div>
          </div>
        )}

        {/* =========================================================
            📖 TAB: E-CATALOG & STOCK (แคตตาล็อก & สต๊อก)
            ========================================================= */}
        {activeTab === 'catalog' && (
          <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h2 className="text-2xl font-black text-white">📖 แคตตาล็อกสินค้า & สต๊อกอัปเดตเรียลไทม์</h2>
                <p className="text-xs text-slate-400 mt-1">ดูรุ่น รายละเอียด ขนาด และปริมาณคงเหลือในโกดังคลังสินค้า</p>
              </div>
              <div className="relative w-full md:w-64">
                <FiSearch className="absolute left-3 top-3.5 text-slate-500" />
                <input type="text" placeholder="ค้นหาแบรนด์, รุ่นแอร์..." value={searchCatalog} onChange={e => setSearchCatalog(e.target.value)} className="w-full bg-slate-950/50 border border-slate-800 text-sm rounded-xl py-2.5 pl-10 pr-4 outline-none focus:border-blue-500 text-slate-200" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {products
                .filter(p => p.model_name.toLowerCase().includes(searchCatalog.toLowerCase()) || p.brand.toLowerCase().includes(searchCatalog.toLowerCase()))
                .map(product => (
                  <div key={product.id} className="bg-slate-950/50 border border-slate-800 p-5 rounded-2xl flex gap-4 hover:border-slate-700 transition">
                    <div className="w-20 h-20 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-3xl select-none shrink-0">
                      {product.image_url?.includes('Daikin') ? '⚡' : product.image_url?.includes('Mitsubishi') ? '🌀' : product.image_url?.includes('Carrier') ? '❄️' : '🍃'}
                    </div>
                    <div className="flex-1 space-y-1.5 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2 py-0.5 rounded-md font-bold uppercase">{product.brand}</span>
                        <span className={`text-[10px] font-black border px-2 py-0.5 rounded-md ${product.stock > 10 ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : product.stock > 0 ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' : 'bg-rose-500/10 text-rose-400 border-rose-500/20'}`}>
                          {product.stock > 0 ? `มีสต๊อก ${product.stock} เครื่อง` : 'สินค้าหมด'}
                        </span>
                      </div>
                      <h4 className="font-bold text-sm text-slate-200 truncate">{product.model_name}</h4>
                      <p className="text-[10px] text-slate-400 line-clamp-2">{product.specs}</p>
                      
                      <div className="flex items-center justify-between pt-2 border-t border-slate-900">
                        <span className="text-xs text-slate-500">ขนาด {product.btu.toLocaleString()} BTU</span>
                        <span className="text-sm font-black text-blue-400">฿{Number(product.price).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* =========================================================
            🔔 TAB: REMINDERS (รอบแจ้งเตือนล้างแอร์)
            ========================================================= */}
        {activeTab === 'reminders' && (
          <div className="space-y-6 animate-fade-in">
            <div>
              <h2 className="text-2xl font-black text-white">🔔 ตารางแจ้งเตือนล้างแอร์ลูกค้าเก่า (5.5 เดือน+)</h2>
              <p className="text-xs text-slate-400 mt-1">รายชื่อลูกค้าเก่าที่ทำบริการครบ 5 เดือนครึ่งถึงเวลาทักเพื่อเสนอโปรโมชั่นล้างแอร์</p>
            </div>

            {reminders.length > 0 ? (
              <div className="space-y-3">
                {reminders.map(reminder => (
                  <div key={reminder.id} className="bg-slate-950/50 border border-slate-800 p-4 rounded-2xl flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <h4 className="font-bold text-sm text-slate-200">{reminder.customerName}</h4>
                        <span className="text-[9px] bg-rose-500/10 text-rose-400 border border-rose-500/20 px-2 py-0.5 rounded-md font-bold">
                          ครบกำหนดล้าง ({reminder.days_since_service} วันที่แล้ว)
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-400">
                        <span className="flex items-center gap-1"><FiPhone size={12} /> {reminder.phone}</span>
                        <span className="flex items-center gap-1"><FiMapPin size={12} /> {reminder.address}</span>
                        <span className="flex items-center gap-1"><FiCalendar size={12} /> บริการล่าสุดเมื่อ: {new Date(reminder.appointmentDate).toLocaleDateString('th-TH')}</span>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button onClick={() => handleFollowUpReminder(reminder)} className="flex-1 md:flex-none bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold py-2.5 px-4 rounded-xl shadow-md transition flex items-center justify-center gap-1.5">
                        <FiCopy /> ทักคุย & เปิดออเดอร์ใหม่
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-slate-950/50 border border-slate-800 p-10 rounded-2xl text-center space-y-2">
                <p className="text-sm font-bold text-slate-400">🎉 ยังไม่มีการแจ้งเตือนในรอบเวลานี้</p>
                <p className="text-xs text-slate-500">ไม่มีลูกค้ารายใดครบกำหนดรอบล้างแอร์ 5.5 เดือนที่ไม่ได้ทำนัดจองคิวครั้งใหม่</p>
              </div>
            )}
          </div>
        )}

      </main>

      {/* 📱 แถบนำทางด้านล่าง สำหรับหน้าจอมือถือ (Bottom Navigation Bar) */}
      <nav className="md:hidden bg-slate-950 border-t border-slate-800 fixed bottom-0 left-0 right-0 h-16 flex justify-around items-center z-50">
        <button onClick={() => setActiveTab('dashboard')} className={`flex flex-col items-center gap-1 text-[10px] font-bold ${activeTab === 'dashboard' ? 'text-blue-500' : 'text-slate-500'}`}>
          <FiHome size={20} /> แดชบอร์ด
        </button>
        <button onClick={() => setActiveTab('create_order')} className={`flex flex-col items-center gap-1 text-[10px] font-bold ${activeTab === 'create_order' ? 'text-blue-500' : 'text-slate-500'}`}>
          <FiPlusCircle size={20} /> สร้างออเดอร์
        </button>
        <button onClick={() => setActiveTab('quotation')} className={`flex flex-col items-center gap-1 text-[10px] font-bold ${activeTab === 'quotation' ? 'text-blue-500' : 'text-slate-500'}`}>
          <FiFileText size={20} /> ใบเสนอราคา
        </button>
        <button onClick={() => setActiveTab('catalog')} className={`flex flex-col items-center gap-1 text-[10px] font-bold ${activeTab === 'catalog' ? 'text-blue-500' : 'text-slate-500'}`}>
          <FiBookOpen size={20} /> สต๊อกแอร์
        </button>
        <button onClick={() => setActiveTab('reminders')} className={`flex flex-col items-center gap-1 text-[10px] font-bold relative ${activeTab === 'reminders' ? 'text-blue-500' : 'text-slate-500'}`}>
          <FiBell size={20} />
          {reminders.length > 0 && <span className="absolute -top-1 right-2 w-4 h-4 rounded-full bg-red-600 text-white text-[9px] flex items-center justify-center font-bold">{reminders.length}</span>}
          เตือนล้าง
        </button>
      </nav>

    </div>
  );
}
